import React, { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import placeholderImage from '../../../assets/icons/placeholder-preview.svg';
import s from './Inventory.module.css';
import { useInventoryStatusesData } from './inventoryStatusesData';
import { sendDeleteInventoryRequest } from '../../store/slices/boardSlice';
import { setOpenedInventoryGuid } from '../../store/slices/inventorySlice';
import { EditInventoryModal } from '../EditInventoryModal/EditInventoryModal';
import { Overlay } from '../Overlay/Overlay';
import { QuickEditInventoryModal } from '../QuickEditInventoryModal/QuickEditInventoryModal';

const InventoryImage = React.memo(({ image }) => (
    <div className={s.inventoryImageWrapper}>
        <img src={image} className={s.inventoryImage} alt="inventory-photo" draggable="false" />
    </div>
));

const InventoryStatus = ({ inventoryData, freeDate, status }) => {
    const { t } = useTranslation();
    return (
        <>
            {status !== 0 && (
                <span className={s.inventoryStatus} style={{ color: inventoryData.color }}>
                    <span>{inventoryData.text}</span>
                </span>
            )}
            {status === 0 && freeDate && (
                <span className={s.inventoryStatus} style={{ color: inventoryData.color }}>
                    <span className={s.inventoryStatusText}>{t('InventoryStatus.text')}</span>{' '}
                    <span>{moment(freeDate).format('DD.MM.YYYY')}</span>
                </span>
            )}
        </>
    );
};

const Card = React.memo(
    ({
        image,
        setImage,
        status,
        amount,
        title,
        freeDate,
        inventoryData,
        onEditButtonClick,
        guid,
        isEditWarehouseMode,
        isQuickEditInventoryMode,
        warehouseId,
        deleteInventory,
    }) => {
        const dispatch = useDispatch();

        const onInventoryClick = (e) => {
            if (isQuickEditInventoryMode || isEditWarehouseMode) {
                return;
            }
            dispatch(setOpenedInventoryGuid(e.currentTarget.dataset.guid));
        };
        const inventoryGuid = useSelector((state) => state.inventory.inventoryGuid);

        useEffect(
            () => () => {
                if (inventoryGuid === guid) {
                    dispatch(setOpenedInventoryGuid(null));
                }
            },
            [inventoryGuid, guid]
        );

        return (
            <>
                <div
                    className={s.inventoryBlock}
                    data-status={status}
                    data-guid={guid}
                    onClick={onInventoryClick}
                >
                    <div
                        className={s.inventoryWrapper}
                        style={{ backgroundColor: inventoryData.color }}
                    >
                        <div className={s.inventory}>
                            <InventoryImage image={image} />
                            <div className={s.inventoryDataWrapper}>
                                <div className={s.inventoryDescription}>
                                    <h3
                                        className={cx(s.inventoryTitle, {
                                            [s.decommissionedTitle]: status === 3,
                                        })}
                                    >
                                        {title}
                                    </h3>
                                </div>
                                <div className={s.footer}>
                                    <InventoryStatus
                                        inventoryData={inventoryData}
                                        freeDate={freeDate}
                                        status={status}
                                    />
                                    <span className={s.amount}>
                                        <span
                                            className={s.ellipsisAmount}
                                            title={numberFormatter.formatNumber(amount, 3)}
                                        >
                                            {numberFormatter.formatNumber(amount, 3)}
                                        </span>
                                    </span>
                                </div>
                            </div>
                            {!isEditWarehouseMode && !isQuickEditInventoryMode && (
                                <div
                                    className={s.editInventoryButton}
                                    onClick={onEditButtonClick}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {inventoryGuid === guid && (
                    <Overlay>
                        <EditInventoryModal
                            setImage={setImage}
                            inventoryGuid={guid}
                            warehouseId={warehouseId}
                            deleteInventory={deleteInventory}
                        />
                    </Overlay>
                )}
            </>
        );
    }
);

export const Inventory = (props) => {
    const [isQuickEditInventoryMode, setIsQuickEditInventoryMode] = useState(false);
    const [isInventoryDeleteMode, setIsDeleteInventoryMode] = useState(false);
    const [top, setTop] = useState(false);
    const [left, setLeft] = useState(false);
    const [height, setHeight] = useState(false);
    const [width, setWidth] = useState(false);
    const [isLeftModeModal, setIsLeftModeModal] = useState(false);
    const { t } = useTranslation();

    const ref = useRef(null);
    const dispatch = useDispatch();
    const warehouseData = useSelector((state) => state.board);

    const onEditButtonClick = (e) => {
        e.stopPropagation();
        const rect = ref.current.getBoundingClientRect();
        if (e.currentTarget === e.target) {
            setIsQuickEditInventoryMode(true);
            setTop(rect.top);
            setLeft(rect.left);
            setHeight(rect.height);
            setWidth(rect.width);
        }
        const MODAL_WIDTH = 130;

        setIsLeftModeModal(
            document.documentElement.clientWidth - rect.left - rect.width < MODAL_WIDTH
        );
    };

    const hideOverlay = () => {
        setIsQuickEditInventoryMode(false);
    };

    const deleteInventory = () => {
        setIsDeleteInventoryMode(true);
        swal({
            text: t('Inventory.deleteInventory.text'),
            buttons: {
                confirm: {
                    text: t('Inventory.deleteInventory.deleteBtn.text'),
                    value: true,
                    visible: true,
                    className: 'btn btn-danger',
                    closeModal: true,
                },
                cancel: {
                    text: t('Inventory.deleteInventory.cancelBtn.text'),
                    value: null,
                    visible: true,
                    className: 'btn btn-secondary',
                    closeModal: true,
                },
            },
        }).then((isConfirm) => {
            if (isConfirm) {
                const { warehouseId, inventory } = props;
                const inventoryPosition = warehouseData
                    .find((item) => item.id === warehouseId)
                    .items.findIndex((item) => item.guid === inventory.guid);
                dispatch(
                    sendDeleteInventoryRequest({
                        inventoryPosition,
                        warehouseId: props.warehouseId,
                        inventoryGuid: props.inventory.guid,
                    })
                );
            } else {
                setIsDeleteInventoryMode(false);
                hideOverlay();
            }
        });
    };

    const { inventory, warehouseId, isEditWarehouseMode } = props;
    const { amount, status, title, guid } = inventory;
    const freeDate = inventory.free_date;
    // const image = inventory.preview_url || placeholderImage;
    const [image, setImage] = useState(inventory.preview_url || placeholderImage);

    const { inventoryStatusesData } = useInventoryStatusesData();

    const inventoryData = inventoryStatusesData.find((item) => item.status === status);

    return (
        <>
            <div ref={ref}>
                <Card
                    inventoryData={inventoryData}
                    freeDate={freeDate}
                    onEditButtonClick={onEditButtonClick}
                    image={image}
                    setImage={setImage}
                    status={status}
                    amount={amount}
                    title={title}
                    guid={guid}
                    isEditWarehouseMode={isEditWarehouseMode}
                    isQuickEditInventoryMode={isQuickEditInventoryMode}
                    warehouseId={warehouseId}
                    deleteInventory={deleteInventory}
                />
            </div>
            {!isInventoryDeleteMode && isQuickEditInventoryMode && (
                <Overlay
                    onClick={() => {
                        setIsQuickEditInventoryMode(false);
                    }}
                >
                    <div
                        style={{
                            position: 'fixed',
                            left,
                            top,
                            width,
                            height,
                        }}
                    >
                        <Card
                            inventoryData={inventoryData}
                            freeDate={freeDate}
                            onEditButtonClick={onEditButtonClick}
                            image={image}
                            status={status}
                            amount={amount}
                            title={title}
                            guid={guid}
                            isEditWarehouseMode={isEditWarehouseMode}
                            isQuickEditInventoryMode={isQuickEditInventoryMode}
                            warehouseId={warehouseId}
                            deleteInventory={deleteInventory}
                        />
                        {isQuickEditInventoryMode && (
                            <QuickEditInventoryModal
                                status={status}
                                inventoryData={inventoryData}
                                inventory={inventory}
                                warehouseId={warehouseId}
                                hideOverlay={hideOverlay}
                                deleteInventory={deleteInventory}
                                isLeftModeModal={isLeftModeModal}
                            />
                        )}
                    </div>
                </Overlay>
            )}
        </>
    );
};
